// The landing strip's "Recommended For You" slice.
//
// Separate from `creativeAiStore` on purpose: the analysis list and the recommendation run have
// different lifecycles (the list is refetched by SSE terminal frames, the strip is not) and the
// list store is already the feature's busiest. The generation counter + scope guard are copied from
// that store so a workspace switch mid-flight cannot land a stale section.
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { CreativeAiScope } from '../api/CreativeAiApi'
import { recommendationApi } from '../api/RecommendationApi'
import type { RecommendationSection } from '../models/recommendation.types'
import { mapExecutionToSection } from '../service/recommendations'

import { ApiError } from '@/shared/http'
import type { UiAsyncState } from '@/shared/ui'

function isCanceled(cause: unknown): boolean {
  return ApiError.isApiError(cause) && cause.kind === 'canceled'
}

export const useRecommendationStore = defineStore('creativeAiRecommendations', () => {
  const section = ref<RecommendationSection | null>(null)
  const state = ref<UiAsyncState>('empty')

  let generation = 0

  /** v3 rendered the strip only when it had cards; there is no loading skeleton and no error
   *  state for this surface — a failed run is simply absent (`fetchLatestRecommendations`). */
  const hasRecommendations = computed(() => (section.value?.recommendations.length ?? 0) > 0)

  async function loadLatest(
    scope: CreativeAiScope,
    options: { signal?: AbortSignal } = {},
  ): Promise<void> {
    const requestGeneration = ++generation
    state.value = 'loading'
    try {
      const execution = await recommendationApi.getLatest(scope, options.signal)
      if (requestGeneration !== generation || options.signal?.aborted) return
      section.value = mapExecutionToSection(execution)
      state.value = section.value ? 'ready' : 'empty'
    } catch (cause) {
      if (requestGeneration !== generation || options.signal?.aborted || isCanceled(cause)) return
      // Legacy parity: the strip swallows its own failure. No toast, no retry affordance — the
      // section just does not render, because it is an upsell surface, not the page's data.
      section.value = null
      state.value = 'error'
    }
  }

  function resetState(): void {
    generation += 1
    section.value = null
    state.value = 'empty'
  }

  return { section, state, hasRecommendations, loadLatest, resetState }
})

export type RecommendationStore = ReturnType<typeof useRecommendationStore>
